<template>
  <div class="dial">
    <svg viewBox="0 0 300 300" role="img" :aria-label="unit + ': ' + odo">
      <defs>
        <radialGradient :id="uid + 'bezel'" cx="0.42" cy="0.32" r="0.75">
          <stop offset="0" stop-color="#ffffff" />
          <stop offset="0.72" stop-color="#f3f4f6" />
          <stop offset="1" stop-color="#c3c7cd" />
        </radialGradient>
        <linearGradient :id="uid + 'shine'" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#ffffff" />
          <stop offset="1" stop-color="#a9aeb5" />
        </linearGradient>
        <linearGradient :id="uid + 'rim'" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#9da2a9" />
          <stop offset="0.5" stop-color="#e4e6e9" />
          <stop offset="1" stop-color="#8e939a" />
        </linearGradient>
        <radialGradient :id="uid + 'face'" cx="0.5" cy="0.45" r="0.6">
          <stop offset="0" stop-color="#ffffff" />
          <stop offset="0.82" stop-color="#f7f8f9" />
          <stop offset="1" stop-color="#e2e5e9" />
        </radialGradient>
        <linearGradient :id="uid + 'needle'" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#b8180c" />
          <stop offset="0.5" stop-color="#ff3d2b" />
          <stop offset="1" stop-color="#b8180c" />
        </linearGradient>
        <radialGradient :id="uid + 'cap'" cx="0.36" cy="0.3" r="0.75">
          <stop offset="0" stop-color="#ff8a74" />
          <stop offset="0.45" stop-color="#e2321f" />
          <stop offset="1" stop-color="#9c1407" />
        </radialGradient>
        <linearGradient :id="uid + 'drum'" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#6b7079" />
          <stop offset="0.22" stop-color="#2a2d33" />
          <stop offset="0.78" stop-color="#2a2d33" />
          <stop offset="1" stop-color="#5f646c" />
        </linearGradient>
        <linearGradient :id="uid + 'glass'" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.75" />
          <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
        </linearGradient>
        <filter :id="uid + 'shadow'" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2.5" dy="4" stdDeviation="2.4" flood-color="#000" flood-opacity="0.35" />
        </filter>
        <filter :id="uid + 'inner'" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>

      <!-- glossy white bezel, metal rim, white face -->
      <circle cx="150" cy="150" r="149" :fill="'url(#' + uid + 'bezel)'" />
      <circle cx="150" cy="150" r="147.5" fill="none" :stroke="'url(#' + uid + 'shine)'" stroke-width="1.5" />
      <circle cx="150" cy="150" r="133" :fill="'url(#' + uid + 'rim)'" />
      <circle cx="150" cy="150" r="128.5" fill="#6c7178" />
      <circle cx="150" cy="150" r="127" :fill="'url(#' + uid + 'face)'" />
      <circle cx="150" cy="150" r="124" fill="none" stroke="#000" stroke-opacity="0.13" stroke-width="5" :filter="'url(#' + uid + 'inner)'" />

      <!-- the scale: a ladder band like the reference, red past the limit -->
      <path v-if="red != null" :d="arc(110, angle(red), angle(max))" fill="none" stroke="#e53935" stroke-opacity="0.16" stroke-width="12" />
      <path :d="arc(116, angle(0), angle(max))" fill="none" stroke="#3a3e45" stroke-width="1.3" />
      <path :d="arc(104, angle(0), angle(max))" fill="none" stroke="#3a3e45" stroke-width="1.1" />
      <line v-for="t in ticks" :key="'t' + t.i" :x1="t.x1" :y1="t.y1" :x2="t.x2" :y2="t.y2"
            :stroke="t.red ? '#d32f2f' : '#33373d'" :stroke-width="t.w" stroke-linecap="butt" />
      <text v-for="t in labels" :key="'n' + t.v" :x="t.x" :y="t.y" text-anchor="middle" dominant-baseline="central"
            class="lbl" :fill="red != null && t.v > red ? '#c62828' : '#383c42'">{{ t.text }}</text>

      <text x="150" y="124" text-anchor="middle" class="unit">{{ unit }}</text>

      <!-- highest value seen while the page is open -->
      <polygon v-if="peak != null && peak > 0" :points="peakMark" fill="#ff9800" stroke="#e65100" stroke-width="0.6" />

      <!-- odometer -->
      <g :transform="'translate(' + (150 - odoWidth / 2) + ', 196)'">
        <rect x="-4" y="-4" :width="odoWidth + 8" height="34" rx="4" fill="#d5d8dc" stroke="#9aa0a7" stroke-width="1" />
        <rect x="-1.5" y="-1.5" :width="odoWidth + 3" height="29" rx="2.5" fill="#15171a" />
        <g v-for="(c, i) in odoCells" :key="'o' + i" :transform="'translate(' + c.x + ', 0)'">
          <rect v-if="c.digit" x="0" y="0" :width="c.w" height="26" rx="1.5" :fill="'url(#' + uid + 'drum)'" />
          <text :x="c.w / 2" y="14" text-anchor="middle" dominant-baseline="central" class="odo">{{ c.ch }}</text>
        </g>
      </g>
      <text x="150" y="246" text-anchor="middle" class="sub">{{ sub }}</text>

      <!-- needle (bouncy), with a little engine idle shake -->
      <g :style="needleStyle">
        <g :class="{ idle: !still }" style="transform-origin: 150px 150px; transform-box: view-box;">
          <polygon points="149.3,34 150.7,34 153.4,150 150,178 146.6,150" :fill="'url(#' + uid + 'needle)'" :filter="'url(#' + uid + 'shadow)'" />
        </g>
      </g>
      <circle cx="150" cy="150" r="13" :fill="'url(#' + uid + 'cap)'" :filter="'url(#' + uid + 'shadow)'" />
      <ellipse cx="146" cy="145.5" rx="5" ry="3" fill="#ffffff" fill-opacity="0.35" />

      <!-- glass reflection -->
      <path d="M 40 112 A 118 118 0 0 1 260 112 Q 150 84 40 112 Z" :fill="'url(#' + uid + 'glass)'" opacity="0.55" />
    </svg>
    <div class="mood">{{ mood }}</div>
  </div>
</template>

<script>
var reduced = typeof window != 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default {
  props: {
    value: { type: Number, default: 0 },
    max: { type: Number, required: true },
    step: { type: Number, required: true },    // a numbered tick every `step`
    minor: { type: Number, default: 9 },       // small ticks between numbered ones
    sweep: { type: Number, default: 224 },     // degrees from 0 to max
    red: { type: Number, default: null },      // red part of the scale starts here
    unit: { type: String, default: '' },       // small text on the face, like "km/h"
    odo: { type: String, default: '' },        // the counter's characters
    sub: { type: String, default: '' },
    mood: { type: String, default: '' },
    peak: { type: Number, default: null },
  },
  data() {
    return {
      uid: 'g' + Math.random().toString(36).slice(2, 8) + '-',
      // ignition self-test: the needle swings to the end and back when the dial first shows
      boot: !reduced,
    };
  },
  mounted() {
    if (this.boot) setTimeout(() => { this.boot = false; }, 900);
  },
  computed: {
    shown() { return this.boot ? this.max : Math.max(0, Math.min(this.value, this.max)); },
    still() { return reduced || this.boot; },
    needleStyle() {
      return {
        transform: 'rotate(' + this.angle(this.shown) + 'deg)',
        transformOrigin: '150px 150px',
        transformBox: 'view-box',
        transition: reduced ? 'none' : 'transform 1.1s cubic-bezier(.34,1.56,.64,1)',
      };
    },
    ticks() {
      var out = [], per = this.minor + 1, n = Math.round(this.max / this.step) * per;
      for (var i = 0; i <= n; i++) {
        var v = (this.max * i) / n, major = i % per == 0, half = per % 2 == 0 && i % per == per / 2;
        var a = this.angle(v), p1 = this.pt(116, a), p2 = this.pt(major ? 94 : half ? 100 : 104, a);
        out.push({ i: i, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, w: major ? 3.4 : half ? 2 : 1.1, red: this.red != null && v > this.red });
      }
      return out;
    },
    labels() {
      var out = [];
      for (var v = 0; v <= this.max + 1e-9; v += this.step) {
        var p = this.pt(78, this.angle(v));
        out.push({ v: v, x: p.x, y: p.y, text: String(Math.round(v)) });
      }
      return out;
    },
    peakMark() {
      var a = this.angle(Math.min(this.peak, this.max));
      var tip = this.pt(117, a), l = this.pt(125, a - 2.8), r = this.pt(125, a + 2.8);
      return [tip, l, r].map(function(p) { return p.x + ',' + p.y; }).join(' ');
    },
    odoCells() {
      var x = 0;
      return this.odo.split('').map(function(ch) {
        var digit = /[0-9—-]/.test(ch), w = digit ? 17 : 8;
        var c = { ch: ch, digit: digit, x: x, w: w };
        x += w + (digit ? 1.5 : 0.5);
        return c;
      });
    },
    odoWidth() {
      var c = this.odoCells[this.odoCells.length - 1];
      return c ? c.x + c.w : 17;
    },
  },
  methods: {
    angle(v) { return -this.sweep / 2 + this.sweep * Math.max(0, Math.min(v / this.max, 1)); },
    pt(r, a) {
      var rad = (a * Math.PI) / 180;
      return { x: Math.round((150 + r * Math.sin(rad)) * 100) / 100, y: Math.round((150 - r * Math.cos(rad)) * 100) / 100 };
    },
    arc(r, a1, a2) {
      if (a2 - a1 < 0.01) return '';
      var p1 = this.pt(r, a1), p2 = this.pt(r, a2);
      return 'M ' + p1.x + ' ' + p1.y + ' A ' + r + ' ' + r + ' 0 ' + (a2 - a1 > 180 ? 1 : 0) + ' 1 ' + p2.x + ' ' + p2.y;
    },
  },
};
</script>

<style scoped>
.dial { display: flex; flex-direction: column; align-items: center; }
.dial svg { width: 100%; height: auto; display: block; filter: drop-shadow(0 14px 18px rgba(30, 36, 48, 0.28)); overflow: visible; }
.lbl { font-family: 'Arial Narrow', Arial, Helvetica, sans-serif; font-size: 17px; font-weight: 700; }
.unit { fill: #4a4f57; font-size: 12px; font-weight: 700; }
.sub { fill: #6b717b; font-size: 12.5px; }
.odo { fill: #ffffff; font-family: 'Arial Narrow', Arial, Helvetica, sans-serif; font-size: 19px; font-weight: 700; }
.mood { margin-top: 4px; color: #3d434d; font-size: 15px; font-weight: 600; min-height: 22px; text-align: center; }
.idle { animation: idle 0.13s ease-in-out infinite alternate; }
@keyframes idle {
  from { transform: rotate(-0.5deg); }
  to { transform: rotate(0.5deg); }
}
@media (prefers-reduced-motion: reduce) {
  .idle { animation: none; }
}
</style>
